import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Play, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { recipesAPI } from "@/services/api/recipes";
import {
  recipeSchedulesAPI,
  type RecipeSchedule,
} from "@/services/api/recipeSchedules";
import { toast } from "@/providers/NotificationProvider";
import {
  BuildInsetCard,
  BuildSectionCard,
} from "@/components/build/BuildChrome";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Input } from "@/components/ui/Input";
import { dedupeRecipesById, recipeListKey } from "@/utils/recipeList";

export default function DeploySchedulesSection({
  organizationId,
  deploymentId,
  workflowId,
}: {
  organizationId: string;
  deploymentId: string;
  /** Scope the section to one Workflow (Workflow › API & triggers). */
  workflowId?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [selectedRecipeId, setRecipeId] = useState("");
  const recipeId = workflowId ?? selectedRecipeId;
  const [type, setType] = useState("cron");
  const [cronExpression, setCronExpression] = useState("0 9 * * *");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduleToDelete, setScheduleToDelete] = useState<RecipeSchedule | null>(
    null,
  );

  const schedulesQuery = useQuery({
    queryKey: ["recipe-schedules", organizationId, deploymentId],
    queryFn: () => recipeSchedulesAPI.list(organizationId, deploymentId),
  });
  const recipesQuery = useQuery({
    queryKey: ["deployment-recipes", organizationId, deploymentId],
    queryFn: () =>
      recipesAPI.listRecipes(organizationId, deploymentId, {
        includeInactive: true,
      }),
    enabled: !workflowId,
  });
  const schedules = (schedulesQuery.data || []).filter(
    (schedule) => !workflowId || schedule.recipe_id === workflowId,
  );
  const recipes = dedupeRecipesById(recipesQuery.data || []);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["recipe-schedules", organizationId, deploymentId],
    });

  const createSchedule = useMutation({
    mutationFn: () =>
      recipeSchedulesAPI.create(organizationId, deploymentId, {
        name: name.trim(),
        recipe_id: recipeId,
        type: type === "cron" ? "PERIODIC" : "SCHEDULED",
        cron_expression: type === "cron" ? cronExpression : undefined,
        scheduled_time:
          type === "scheduled" && scheduledTime
            ? new Date(scheduledTime).toISOString()
            : undefined,
        payload: {},
        enabled: true,
      }),
    onSuccess: () => {
      setName("");
      invalidate();
      toast.success(t("build.scheduleCreated", { defaultValue: "Schedule créé" }));
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteSchedule = useMutation({
    mutationFn: (schedule: RecipeSchedule) =>
      recipeSchedulesAPI.delete(organizationId, deploymentId, schedule.id),
    onSuccess: invalidate,
    onError: (error: Error) => toast.error(error.message),
  });

  const runSchedule = useMutation({
    mutationFn: (schedule: RecipeSchedule) =>
      recipeSchedulesAPI.run(organizationId, deploymentId, schedule.id),
    onSuccess: () =>
      toast.success(
        t("build.scheduleRunStarted", { defaultValue: "Schedule lancé" }),
      ),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-4" data-testid="workflow-schedules">
      <BuildSectionCard className="space-y-4">
        <h2 className="text-xl font-semibold text-foreground">
          {t("build.scheduleCreateTitle", { defaultValue: "Créer un schedule" })}
        </h2>
        <div
          className={
            workflowId
              ? "grid gap-3 lg:grid-cols-[1fr_150px_1fr_auto]"
              : "grid gap-3 lg:grid-cols-[1fr_1fr_150px_1fr_auto]"
          }
        >
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("build.scheduleName", { defaultValue: "Nom" })}
          />
          {workflowId ? null : (
            <select
              value={recipeId}
              onChange={(event) => setRecipeId(event.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">{t("build.scheduleSelectWorkflow", { defaultValue: "Workflow" })}</option>
              {recipes.map((recipe, index) => (
                <option key={recipeListKey(recipe, index)} value={recipe.id}>
                  {recipe.name || recipe.id}
                </option>
              ))}
            </select>
          )}
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="cron">Cron</option>
            <option value="scheduled">{t("build.scheduleOnce", { defaultValue: "Date" })}</option>
          </select>
          {type === "cron" ? (
            <Input
              value={cronExpression}
              onChange={(event) => setCronExpression(event.target.value)}
              placeholder="0 9 * * *"
            />
          ) : (
            <Input
              type="datetime-local"
              value={scheduledTime}
              onChange={(event) => setScheduledTime(event.target.value)}
            />
          )}
          <Button
            type="button"
            disabled={!name.trim() || !recipeId || createSchedule.isPending}
            onClick={() => createSchedule.mutate()}
          >
            {createSchedule.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t("common.create")}
          </Button>
        </div>
      </BuildSectionCard>

      <BuildSectionCard className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {t("build.scheduleListTitle", { defaultValue: "Schedules actifs" })}
          </h2>
          {schedulesQuery.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : null}
        </div>
        <div className="grid gap-3">
          {schedules.length === 0 ? (
            <BuildInsetCard className="text-sm text-muted-foreground">
              {t("build.scheduleEmpty", { defaultValue: "Aucun schedule configuré." })}
            </BuildInsetCard>
          ) : (
            schedules.map((schedule) => (
              <div
                key={schedule.id}
                className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 lg:flex-row lg:items-center lg:justify-between"
              >
                <div>
                  <p className="font-medium text-foreground">{schedule.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {schedule.type} ·{" "}
                    {schedule.cron_expression ||
                      schedule.scheduled_time ||
                      schedule.webhook_url ||
                      "-"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={schedule.enabled === false ? "outline" : "default"}
                  >
                    {schedule.enabled === false
                      ? t("common.disabled")
                      : t("common.active")}
                  </Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={t("build.scheduleRun", { defaultValue: "Lancer" })}
                    onClick={() => runSchedule.mutate(schedule)}
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.delete")}
                    onClick={() => setScheduleToDelete(schedule)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </BuildSectionCard>
      <ConfirmDialog
        open={Boolean(scheduleToDelete)}
        onOpenChange={(open) => {
          if (!open) setScheduleToDelete(null);
        }}
        title={t("build.scheduleDeleteConfirm", {
          defaultValue: "Supprimer ce schedule ?",
        })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        variant="destructive"
        disabled={deleteSchedule.isPending}
        onConfirm={() => {
          if (!scheduleToDelete) return;
          deleteSchedule.mutate(scheduleToDelete);
          setScheduleToDelete(null);
        }}
      />
    </div>
  );
}
